/* eslint-disable react-hooks/exhaustive-deps */
import FileUpload from "components/FileUpload";
import { useTooltip } from "context/TooltipProvider";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { editInstruction2 } from "services/doubtfulService";
import { DoubtfulCase } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  instruction: DoubtfulCase;
  onClose: (refetch: boolean) => void;
};

const AskingTeamDecisionForm: FC<Props> = ({ instruction, onClose }) => {
  const { t } = useTranslation();
  const { showTooltip } = useTooltip();
  const [formState, setFormState] = useState<DoubtfulCase>({});
  const [loading, setLoading] = useState(false);
  const [decision, setDecision] = useState("Resolved By Ours");

  const RESOLVED_BY_OURS = "Resolved By Ours";
  const FORWARDED_TO_HIGHER_OFFICIALS = "Forwarded To Higher Officials";

  useEffect(() => {
    if (instruction) {
      setFormState({ ...formState, id: instruction?.id, ...instruction });
    }
  }, [instruction]);

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (decision === RESOLVED_BY_OURS) {
      if (!formState.team_resolution_summary)
        return t("asking_team_decision_team_resolution_summary_required");
      else if (!formState.team_resolution_summary_date)
        return t("asking_team_decision_team_resolution_summary_date_required");
      else if (!formState.team_resolution_summary_document)
        return t(
          "asking_team_decision_team_resolution_summary_document_required",
        );
    } else if (decision === FORWARDED_TO_HIGHER_OFFICIALS) {
      if (!formState.draft_document)
        return t("asking_team_decision_draft_document_required");
      else if (!formState.team_draft_summary)
        return t("asking_team_decision_team_draft_summary_required");
      else if (!formState.team_draft_date)
        return t("asking_team_decision_team_draft_date_required");
    }
    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitData = {
      id: formState.id,
      team_resolution_summary:
        decision === RESOLVED_BY_OURS ? formState.team_resolution_summary : "",
      team_resolution_summary_date:
        decision === RESOLVED_BY_OURS
          ? formState.team_resolution_summary_date
          : "",
      team_resolution_summary_document:
        decision === RESOLVED_BY_OURS
          ? formState.team_resolution_summary_document
          : "",

      team_draft_summary:
        decision === FORWARDED_TO_HIGHER_OFFICIALS
          ? formState.team_draft_summary
          : "",
      team_draft_date:
        decision === FORWARDED_TO_HIGHER_OFFICIALS
          ? formState.team_draft_date
          : "",
      draft_document:
        decision === FORWARDED_TO_HIGHER_OFFICIALS
          ? formState.draft_document
          : "",
    };

    editInstruction2({
      ...submitData,
      request_status:
        decision === RESOLVED_BY_OURS ? "Resolved" : "forwarded_to_registrar",
    })
      .then(() => {
        showTooltip(t("successful"), "success");
        setFormState({});
        setLoading(false);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
        setLoading(false);
      });
  };

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleFile = (file: File | null, name: string) => {
    setFormState({ ...formState, [name]: file });
  };

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">
          {instruction?.request_status === "Rejected" ? t("update") : t("add")}{" "}
          {t("decision")}
        </h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          {t("asking_team_decision_subtitle")}
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* Decision Selector */}
          <div className="flex flex-col mt-4 w-full">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              {t("decision")}
            </label>
            <select
              name="decision"
              value={decision}
              onChange={(e) => setDecision(e.target.value)}
              className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
            >
              <option value={RESOLVED_BY_OURS}>
                {t("doubtful_detail_resolved_by_ours")}
              </option>
              <option value={FORWARDED_TO_HIGHER_OFFICIALS}>
                {t("doubtful_detail_forwarded_to_higher_officials")}
              </option>
            </select>
          </div>

          {/* Conditional Fields */}
          {decision === RESOLVED_BY_OURS && (
            <div className="grid grid-cols-1 gap-2 gap-x-4 mt-4">
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("asking_team_decision_team_resolution_summary_date")}
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="team_resolution_summary_date"
                  value={
                    formState?.team_resolution_summary_date
                      ? new Date(formState.team_resolution_summary_date)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("asking_team_decision_team_resolution_summary")}
                </label>
                <textarea
                  rows={4}
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="team_resolution_summary"
                  value={formState?.team_resolution_summary || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <FileUpload
                  label={t(
                    "asking_team_decision_team_resolution_summary_document",
                  )}
                  fileTypes="application/pdf,image/*"
                  info={t("upload_document")}
                  required={true}
                  onChange={(file) =>
                    handleFile(file, "team_resolution_summary_document")
                  }
                />
              </div>
            </div>
          )}

          {decision === FORWARDED_TO_HIGHER_OFFICIALS && (
            <div className="grid grid-cols-1 gap-2 gap-x-4 mt-4">
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("doubtful_detail_draft_date")}
                </label>
                <input
                  type="date"
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="team_draft_date"
                  value={
                    formState?.team_draft_date
                      ? new Date(formState.team_draft_date)
                          .toISOString()
                          .split("T")[0]
                      : ""
                  }
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
                  {t("doubtful_detail_draft_summary")}
                </label>
                <textarea
                  rows={4}
                  className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
                  name="team_draft_summary"
                  value={formState?.team_draft_summary || ""}
                  onChange={handleChange}
                />
              </div>
              <div className="flex flex-col mt-4 w-full">
                <FileUpload
                  label={t("asking_team_decision_draft_document")}
                  fileTypes="application/pdf,image/*"
                  info={t("asking_team_decision_upload_draft_document")}
                  required={true}
                  onChange={(file) => handleFile(file, "draft_document")}
                />
              </div>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              type="submit"
              className={`border border-[#212121] bg-[#212121] px-[76px] py-2 
                                  text-white font-[Poppins] cursor-pointer font-[500] text-base rounded-md hover:bg-[#414040]
                                          ${
                                            loading
                                              ? "opacity-50 cursor-not-allowed"
                                              : ""
                                          }`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  {t("loading")}
                </div>
              ) : (
                <>
                  {instruction?.request_status === "Rejected"
                    ? t("update")
                    : t("add")}{" "}
                  {t("decision")}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AskingTeamDecisionForm;
